/**
 * Meal periods as the packet groups them: one section per mealtime and serve
 * day, food served on the delivery day first ("LUNCH · SERVE TODAY"), food
 * delivered today for a later day boxed apart ("SERVE MON 10/19 · BREAKFAST ·
 * PACK SEPARATELY"; Notes · B1+B2: "Keeping Monday's food in its own box stops
 * it being served Thursday"). Pure; unit-tested.
 */

import { deliverableTypeGroup } from '@/lib/chefs/pack-groups'
import type { Campus, CampusMilkLine, Deliverable, Mealtime, MealtimeBlock } from '@/lib/chefs/types'
import { MEALTIME_LABEL, MEALTIME_ORDER } from '@/lib/chefs/types'

const WEEKDAY = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'] as const
const WEEKDAY_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const

/** `YYYY-MM-DD` as a UTC date: shipment dates are UTC-midnight instants. */
function utcDay(key: string): Date {
  return new Date(`${key}T00:00:00.000Z`)
}

/** "MON 10/19". */
export function serveDayShort(dayKey: string): string {
  const day = utcDay(dayKey)
  return `${WEEKDAY[day.getUTCDay()]} ${day.getUTCMonth() + 1}/${day.getUTCDate()}`
}

/** "Monday". */
export function serveDayLong(dayKey: string): string {
  return WEEKDAY_LONG[utcDay(dayKey).getUTCDay()]!
}

export interface ServeWords {
  /** True when every serve day is after the delivery day: the food is boxed apart. */
  later: boolean
  /**
   * True when the block feeds today AND a later day (a same-day menu and a
   * day-before menu on one campus and mealtime). Its rows merge across those
   * days, so they cannot be boxed apart: every surface says both days and asks
   * for the containers to be labeled by serve day.
   */
  mixed: boolean
  /** "SERVE TODAY", "SERVE MON 10/19", "SERVE TODAY + MON 10/19". */
  label: string
  /** The first later serve day (`YYYY-MM-DD`), or null. */
  laterDay: string | null
}

/**
 * When a block's food is served, against its delivery day. No serve days
 * known reads as today: the packet never invents another day.
 */
export function serveWords(serveDays: readonly string[] | undefined, deliveryIso: string): ServeWords {
  const days = [...new Set(serveDays ?? [])].sort()
  const later = days.filter((day) => day > deliveryIso)
  const today = days.length === 0 || days.some((day) => day <= deliveryIso)
  if (later.length === 0) {
    return { later: false, mixed: false, label: 'SERVE TODAY', laterDay: null }
  }
  const laterLabel = later.map(serveDayShort).join(' + ')
  return today
    ? { later: false, mixed: true, label: `SERVE TODAY + ${laterLabel}`, laterDay: later[0]! }
    : { later: true, mixed: false, label: `SERVE ${laterLabel}`, laterDay: later[0]! }
}

/** "Mon" for a later serve day's key. */
export function serveWeekday(dayKey: string): string {
  const caps = serveDayShort(dayKey).split(' ')[0]!
  return `${caps.charAt(0)}${caps.slice(1).toLowerCase()}`
}

/** "Mon 10/19" for a later serve day's key, as sentences say it. */
export function serveDayWords(dayKey: string): string {
  return `${serveWeekday(dayKey)} ${serveDayShort(dayKey).split(' ')[1]}`
}

/** "LUNCH", "PM SNACK". */
export function mealtimeCaps(mealtime: Mealtime): string {
  return MEALTIME_LABEL[mealtime].toUpperCase()
}

export interface MealSection {
  /** `${mealtime}|${serve label}`: the same meal on another stop shares it. */
  key: string
  mealtime: Mealtime
  serve: ServeWords
  block: MealtimeBlock
}

export function mealSectionKey(mealtime: Mealtime, serve: ServeWords): string {
  return `${mealtime}|${serve.label}`
}

/** Mealtime order, then today's before a later day's, then by the later day. */
export function compareMealSections(
  a: Pick<MealSection, 'mealtime' | 'serve'>,
  b: Pick<MealSection, 'mealtime' | 'serve'>,
): number {
  const byMealtime = MEALTIME_ORDER.indexOf(a.mealtime) - MEALTIME_ORDER.indexOf(b.mealtime)
  if (byMealtime !== 0) {
    return byMealtime
  }
  if (a.serve.later !== b.serve.later) {
    return a.serve.later ? 1 : -1
  }
  return (a.serve.laterDay ?? '').localeCompare(b.serve.laterDay ?? '')
}

/** A campus's meal sections in print order. */
export function campusMealSections(campus: Pick<Campus, 'mealtimes'>, deliveryIso: string): MealSection[] {
  return campus.mealtimes
    .map((block) => {
      const serve = serveWords(block.serveDays, deliveryIso)
      return { key: mealSectionKey(block.mealtime, serve), mealtime: block.mealtime, serve, block }
    })
    .sort(compareMealSections)
}

/** An entrée: the meal itself (a bundle or a main), never a side, extra or condiment. */
export function isEntree(d: Pick<Deliverable, 'productType' | 'components'>): boolean {
  return deliverableTypeGroup(d) === 'mains'
}

/** One meal's share of a milk row, with the day it is poured. */
export interface MilkServeShare {
  serve: ServeWords
  /** The meal it is poured with; null when the row says no mealtime. */
  mealtime: Mealtime | null
  /** `mealSectionKey` of the campus meal it rides with; null when the campus takes no food at that mealtime. */
  sectionKey: string | null
  qty: number
}

/**
 * A milk row split by the meal it is poured with: each mealtime's share
 * follows its meal's serve day (breakfast milk delivered Thursday for Monday
 * is Monday's). One share per meal; `milkSplitWords` adds the shares up by
 * day for the milk table's band.
 */
export function milkServeShares(
  line: Pick<CampusMilkLine, 'byMealtime' | 'qty'>,
  campus: Pick<Campus, 'mealtimes'>,
  deliveryIso: string,
): MilkServeShare[] {
  const sections = campusMealSections(campus, deliveryIso)
  const shares = new Map<string, MilkServeShare>()
  const parts = line.byMealtime.length > 0 ? line.byMealtime : []
  for (const part of parts) {
    const section = sections.find((s) => s.mealtime === part.mealtime) ?? null
    const serve = section?.serve ?? serveWords(undefined, deliveryIso)
    const key = section?.key ?? `milk|${serve.label}`
    const prior = shares.get(key)
    if (prior) {
      prior.qty += part.qty
    } else {
      shares.set(key, { serve, mealtime: part.mealtime, sectionKey: section?.key ?? null, qty: part.qty })
    }
  }
  if (shares.size === 0) {
    return [{ serve: serveWords(undefined, deliveryIso), mealtime: null, sectionKey: null, qty: line.qty }]
  }
  // Today's share first, then each later day's: "today 330 · Mon 325".
  return [...shares.values()].sort(
    (a, b) =>
      Number(a.serve.later) - Number(b.serve.later) || (a.serve.laterDay ?? '').localeCompare(b.serve.laterDay ?? ''),
  )
}

/** "today 330 · Mon 325": a milk row's split, or null when it is all one day's. */
export function milkSplitWords(shares: readonly MilkServeShare[]): string | null {
  // By day, not by meal: breakfast and lunch milk both poured today read "today 530".
  const byDay = new Map<string, number>()
  for (const share of shares) {
    const day = share.serve.later && share.serve.laterDay ? serveWeekday(share.serve.laterDay) : 'today'
    byDay.set(day, (byDay.get(day) ?? 0) + share.qty)
  }
  if (byDay.size <= 1) {
    return null
  }
  return [...byDay.entries()].map(([day, qty]) => `${day} ${qty.toLocaleString()}`).join(' · ')
}
